/**
 * Schedule / Slot / CalDate 类型定义（与 schedule 微服务契约对齐）
 */

/**
 * 拍摄计划状态枚举
 */
export enum ScheduleStatus {
  PREPARING = 'PREPARING',     // 筹备中
  SHOOTING = 'SHOOTING',       // 拍摄中
  COMPLETED = 'COMPLETED',     // 已完成
  PAUSED = 'PAUSED',           // 暂停
  CANCELLED = 'CANCELLED',    // 已取消
}

/**
 * 拍摄计划状态中文标签映射
 */
export const ScheduleStatusLabels: Record<ScheduleStatus, string> = {
  [ScheduleStatus.PREPARING]: '筹备中',
  [ScheduleStatus.SHOOTING]: '拍摄中',
  [ScheduleStatus.COMPLETED]: '已完成',
  [ScheduleStatus.PAUSED]: '暂停',
  [ScheduleStatus.CANCELLED]: '已取消',
};

/**
 * 数据状态枚举
 */
export enum ScheduleDataStatus {
  NORMAL = 'NORMAL',         // 正常
  DELETED = 'DELETED',       // 删除
  ARCHIVED = 'ARCHIVED',     // 归档
}

export interface ScheduleInfo {
  scheduleID: string;
  scheduleName?: string;
  scheduleFilmID?: string;
  scheduleBelongBreakdownID?: string;
  scheduleStatus?: ScheduleStatus;
  scheduleOwnerID?: string;
  scheduleOrgID?: string;
  scheduleAssigneeID?: string;
  scheduleCreatorID?: string;
  scheduleCreateTime?: string;
  scheduleUpdateTime?: string;
  scheduleDataStatus?: ScheduleDataStatus;
  scheduleStartDate?: string;
  scheduleEndDate?: string;
  scheduleTotalDays?: number | null;
}

export interface QueryScheduleParams {
  scheduleID?: string;
  scheduleName?: string;
  scheduleFilmID?: string;
  scheduleBelongBreakdownID?: string;
  scheduleStatus?: ScheduleStatus;
  scheduleOwnerID?: string;
  scheduleOrgID?: string;
  scheduleAssigneeID?: string;
  scheduleCreatorID?: string;
  scheduleDataStatus?: ScheduleDataStatus;
  scheduleStartDate?: string;
  scheduleEndDate?: string;
}

export interface CreateScheduleParams {
  scheduleName?: string;
  scheduleFilmID?: string;
  scheduleBelongBreakdownID?: string;
  scheduleStatus?: ScheduleStatus;
  scheduleOwnerID?: string;
  scheduleOrgID?: string;
  scheduleAssigneeID?: string;
  scheduleOverview?: string;
  scheduleStartDate?: string;
  scheduleEndDate?: string;
}

export interface UpdateScheduleParams {
  scheduleName?: string;
  scheduleFilmID?: string;
  scheduleBelongBreakdownID?: string;
  scheduleStatus?: ScheduleStatus;
  scheduleOwnerID?: string;
  scheduleOrgID?: string;
  scheduleAssigneeID?: string;
  scheduleOverview?: string;
  scheduleDataStatus?: ScheduleDataStatus;
  scheduleStartDate?: string;
  scheduleEndDate?: string;
  scheduleTotalDays?: number | null;
}

export interface CopyScheduleResponse extends ScheduleInfo {
  slots?: SlotInfo[];
  calDates?: CalDateInfo[];
}

export interface ScheduleWithUserNames extends ScheduleInfo {
  scheduleAssigneeName?: string;
  scheduleOwnerName?: string;
  scheduleCreatorName?: string;
}

export enum SlotType {
  SHOOT = 'SHOOT',
  NOTE = 'NOTE'
}

export type ShiftType =
  | 'MORNING'
  | 'AFTERNOON'
  | 'EVENING'
  | 'APPENDIX1'
  | 'APPENDIX2'
  | 'APPENDIX3'
  | 'APPENDIX4'
  | 'APPENDIX5';

export enum SlotStatus {
  PREPARING = 'PREPARING',
  UPCOMING = 'UPCOMING',
  IN_PROGRESS = 'IN_PROGRESS',
  DELAYED = 'DELAYED',
  COMPLETED = 'COMPLETED',
  INCOMPLETE = 'INCOMPLETE'
}

export interface SlotInfo {
  slotID: string;
  slotType?: SlotType;
  slotShift?: ShiftType | null;
  slotLocationVisibility?: string | null;
  slotName?: string;
  slotSceneCode?: string;
  slotBelongSceneID?: string;
  slotBelongScheduleID?: string;
  slotBelongBreakdownID?: string;
  slotLocation?: string;
  slotStatus?: SlotStatus;
  slotDate?: string;
  slotEndDay?: string;
  slotCreatorID?: string;
  slotCreateTime?: string;
  slotUpdateTime?: string;
}

export interface QuerySlotParams {
  slotID?: string;
  slotName?: string;
  slotSceneCode?: string;
  slotBelongSceneID?: string;
  slotBelongScheduleID?: string;
  slotBelongBreakdownID?: string;
  slotDate?: string;
  slotEndDay?: string;
  slotCreatorID?: string;
}

export interface CreateSlotParams {
  slotType?: SlotType;
  slotShift?: ShiftType | null;
  slotLocationVisibility?: string | null;
  slotName?: string;
  slotSceneCode?: string;
  slotBelongSceneID?: string;
  slotBelongScheduleID?: string;
  slotBelongBreakdownID?: string;
  slotLocation?: string;
  slotStatus?: SlotStatus;
  slotDate?: string;
  slotEndDay?: string;
  slotCreatorID?: string;
}

export interface UpdateSlotParams {
  slotType?: SlotType;
  slotShift?: ShiftType | null;
  slotLocationVisibility?: string | null;
  slotName?: string;
  slotSceneCode?: string;
  slotBelongSceneID?: string;
  slotBelongScheduleID?: string;
  slotBelongBreakdownID?: string;
  slotLocation?: string;
  slotStatus?: SlotStatus;
  slotDate?: string;
  slotEndDay?: string;
}

/** 计划日排序：slotID 一维数组 */
export type CalDateSlotsSortIds = string[];

export interface CalDateInfo {
  calDateID: string;
  calDateDt?: string;
  calDateBelongScheduleID?: string;
  calDateTittle?: string;
  calDateShift?: string;
  calDateSlotsSort?: CalDateSlotsSortIds;
  calDateCreateTime?: string;
  calDateUpdateTime?: string;
}

export interface UpsertCalDateParams {
  calDateDt?: string;
  calDateBelongScheduleID?: string;
  calDateTittle?: string;
  calDateShift?: string;
  calDateSlotsSort?: CalDateSlotsSortIds;
}
